import { ColoursObj } from "@eventr/common-storybook";
import { useThemeContext } from "../../state/themeState/themeContext";

/**
 * The interface style ("light" | "dark") for the native liquid-glass chrome (iOS 26
 * toolbar + tab bar), derived from the active theme's primary background luminance.
 * `ColoursObj` is mutated in place on a theme change, so reading it here reflects the
 * current theme; a light background yields "light", a dark one "dark". This covers the
 * light / dark / custom schemes uniformly without hard-coding per-scheme values.
 *
 * We drive the interface style rather than painting bar colours so the blur/translucency
 * of the liquid-glass materials is preserved.
 */
export function nativeInterfaceStyle(): "light" | "dark" | "system" {
  // On Device with no itinerary tag theme the app follows the OS. The style is forced on
  // the window, and WKWebView answers prefers-color-scheme from that window, so pinning it
  // here would make the device look permanently light (or dark) to the web content.
  const { themeOverwrite, selectedColourScheme } = useThemeContext.getState();
  if (!themeOverwrite && selectedColourScheme !== "custom") return "system";
  return isColourDark(ColoursObj.backgrounds.primary) ? "dark" : "light";
}

/** The tab bar's selected-item tint: the theme's secondary (accent) colour. */
export function nativeTabBarTint(): string {
  return ColoursObj.core.secondary;
}

/** YIQ luminance test: true when the colour is dark enough to want light-on-dark chrome. */
function isColourDark(hex: string): boolean {
  const c = hex.replace("#", "");
  if (c.length < 6) return false;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 128;
}
